import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import _isEmpty from 'lodash/isEmpty';
import _reject from 'lodash/reject';
import {
  AdservingPartnerContactType,
  AdServingPartnerData,
  AdservingPartnerInfo_UI,
  AdservingPartnerMacro,
  AdservingPartnerMacro_UI,
  AdServingPartnerType,
  AdservingPartnerWithMacros,
  CreativeSpec_UI,
  DataSharing_UI,
  ExtendedAdServingPartnerData,
  Macros_UI,
  MacroSource,
  RemoteAdServingPartnerData,
  RemoteAdservingPartnerInfo_UI,
  RemoteAdservingPartnerMacro,
  RemoteAdservingPartnerMacro_UI,
  RemoteAdservingPartnerWithMacros,
  RemoteCreativeSpec_UI,
  RemoteDataSharing_UI,
  RemoteExtendedAdServingPartnerData,
  RemoteMacros_UI,
  RemoteTargetPlatformsData_UI,
  RestringTrackingVendorsOption,
  TargetPlatformsData_UI,
  VastVersion,
} from './adServingPartner.types';

function toLocalPartnerData(
  remote: RemoteAdServingPartnerData,
  partnersList: RemoteAdServingPartnerData[],
): AdServingPartnerData {
  return {
    ...remote,
    basedOnAdservingPartnerName: remote.basedOnAdservingPartner
      ? (partnersList.find((partner) => partner.id === remote.basedOnAdservingPartner)
          ?.displayName ?? '')
      : '',
    type: AdServingPartnerType[remote.type].toString(),
    supportedCampaignTypes: remote.supportedCampaignTypes ?? [],
  };
}

function toLocalExtendedPartnerData(
  remote: RemoteExtendedAdServingPartnerData,
): ExtendedAdServingPartnerData {
  return {
    ...remote,
    isBasedOnPartner: !!remote.basedOnAdservingPartner,
    isLimitedCTVVariations: !!remote.maxCTVCampaignVariationsCount,
    description: remote.description ?? '',
    displayName: remote.displayName ?? '',
    supportedVideoVastVersions: remote.supportedVideoVastVersions ?? [],
    supportedAudioVastVersions: remote.supportedAudioVastVersions ?? [],
    supportedCampaignTypes: remote.supportedCampaignTypes ?? [],
    mainContact: remote.contacts.find((ct) => ct.type === AdservingPartnerContactType.Main),
    techContact: remote.contacts.find((ct) => ct.type === AdservingPartnerContactType.Technical),
    accountingContact: remote.contacts.find(
      (ct) => ct.type === AdservingPartnerContactType.Accounting,
    ),
    vendorsApproval: remote.whitelistedTrackingVendorIds.length
      ? RestringTrackingVendorsOption.RestrictVendors
      : RestringTrackingVendorsOption.NoApprovedVendors,
  };
}

function toLocalAdservingPartnerMacro(remote: RemoteAdservingPartnerMacro): AdservingPartnerMacro {
  return {
    ...remote,
    macroSource: remote.isInherited ? MacroSource.Parent : MacroSource.Self,
  };
}

function toRemoteAdservingPartnerMacro(
  macro: AdservingPartnerMacro,
  campaignEnums: Record<string, CLEnumsMapper>,
): RemoteAdservingPartnerMacro {
  return {
    ...macro,
    dspParameter:
      +campaignEnums[CLEnumKeys.CampaignPage.DspParameter].byValue[macro.dspParameter].value,
  };
}

function toRemoteAdServingPartnerWithMacros(
  partner: AdservingPartnerWithMacros,
  campaignEnums: Record<string, CLEnumsMapper>,
): RemoteAdservingPartnerWithMacros {
  return {
    dspUi: {
      ...partner.partner,
      maxCTVCampaignVariationsCount: partner.partner.isLimitedCTVVariations
        ? partner.partner.maxCTVCampaignVariationsCount
        : null,
      supportedCampaignTypes: partner.partner.supportedCampaignTypes,
      supportedVastVersions: partner.partner.supportedVastVersions,
      supportedVideoVastVersions: partner.partner.supportedVideoVastVersions,
      supportedAudioVastVersions: partner.partner.supportedAudioVastVersions,
      supportedClients: partner.partner.supportedClients,
      contacts: _reject(
        [
          {...partner.partner.mainContact, type: AdservingPartnerContactType.Main},
          {...partner.partner.techContact, type: AdservingPartnerContactType.Technical},
          {...partner.partner.accountingContact, type: AdservingPartnerContactType.Accounting},
        ],
        _isEmpty,
      ),
    },
    dspMacrosUi: partner.macros
      .filter((m) => m.macroSource === MacroSource.Self)
      .map((macro) => toRemoteAdservingPartnerMacro(macro, campaignEnums)),
  };
}

function toLocalAdservingPartnerWithMacros(
  remote: RemoteAdservingPartnerWithMacros,
): AdservingPartnerWithMacros {
  return {
    partner: toLocalExtendedPartnerData(remote.dspUi),
    macros: remote.dspMacrosUi.map((macro) => toLocalAdservingPartnerMacro(macro)),
  };
}

function toClientAdservingPartnerInfo(
  remote: RemoteAdservingPartnerInfo_UI,
): AdservingPartnerInfo_UI {
  return {
    id: remote.id,
    displayName: remote.displayName ?? '',
    status: remote.status,
    type: remote.type,
    description: remote.description ?? '',
    basedOnAdservingPartner: remote.basedOnAdservingPartner,
    supportedCampaignTypes: remote.supportedCampaignTypes ?? [],
    clickUrlType: remote.clickUrlType,
    supportedClients: remote.supportedClients ?? [],
    isBasedOnPartner: !!remote.basedOnAdservingPartner,
  };
}

function toRemoteAdservingPartnerInfo(
  client: AdservingPartnerInfo_UI,
): RemoteAdservingPartnerInfo_UI {
  return {
    id: client.id,
    displayName: client.displayName || null,
    status: client.status,
    type: client.type,
    description: client.description || null,
    basedOnAdservingPartner: client.basedOnAdservingPartner,
    supportedCampaignTypes: client.supportedCampaignTypes.length
      ? client.supportedCampaignTypes
      : null,
    clickUrlType: client.clickUrlType,
    supportedClients: client.supportedClients.length ? client.supportedClients : null,
  };
}

function toClientTargetPlatformsData(
  remote: RemoteTargetPlatformsData_UI | null,
): TargetPlatformsData_UI {
  return {
    olvTargetPlatformIds: remote?.olvTargetPlatformIds ?? [],
    ctvTargetPlatformIds: remote?.ctvTargetPlatformIds ?? [],
    defaultOlvId: remote?.defaultOlvId ?? null,
    defaultCtvId: remote?.defaultCtvId ?? null,
  };
}

function toRemoteTargetPlatformsData(client: TargetPlatformsData_UI): RemoteTargetPlatformsData_UI {
  return {
    olvTargetPlatformIds: client.olvTargetPlatformIds.length ? client.olvTargetPlatformIds : null,
    ctvTargetPlatformIds: client.ctvTargetPlatformIds.length ? client.ctvTargetPlatformIds : null,
    defaultOlvId: client.defaultOlvId,
    defaultCtvId: client.defaultCtvId,
  };
}

function toClientCreativeSpec(remote: RemoteCreativeSpec_UI): CreativeSpec_UI {
  return {
    displayRequireNoScriptSupport: remote.displayRequireNoScriptSupport,
    isLimitedCTVVariations: !!remote.maxCTVCampaignVariationsCount,
    maxCTVCampaignVariationsCount: remote.maxCTVCampaignVariationsCount ?? 1,
    displayMaxAnimationLengthInSeconds: remote.displayMaxAnimationLengthInSeconds,
    supportedVideoVastVersions: (
      remote.supportedVideoVastVersions ?? [VastVersion.V2_0, VastVersion.V3_0, VastVersion.V4_0]
    ).map((version) => version.toString()),
    supportedAudioVastVersions: (
      remote.supportedAudioVastVersions ?? [VastVersion.V2_0, VastVersion.V3_0, VastVersion.V4_0]
    ).map((version) => version.toString()),
    targetPlatforms: toClientTargetPlatformsData(remote.targetPlatforms),
  };
}

function toRemoteCreativeSpec(client: CreativeSpec_UI): RemoteCreativeSpec_UI {
  return {
    displayRequireNoScriptSupport: client.displayRequireNoScriptSupport,
    maxCTVCampaignVariationsCount: client.isLimitedCTVVariations
      ? client.maxCTVCampaignVariationsCount
      : null,
    displayMaxAnimationLengthInSeconds: client.displayMaxAnimationLengthInSeconds,
    supportedVideoVastVersions: client.supportedVideoVastVersions.map((version) =>
      typeof version === 'string' ? parseInt(version, 10) : version,
    ) as VastVersion[],
    supportedAudioVastVersions: client.supportedAudioVastVersions.map((version) =>
      typeof version === 'string' ? parseInt(version, 10) : version,
    ) as VastVersion[],
    targetPlatforms: toRemoteTargetPlatformsData(client.targetPlatforms),
  };
}

function toClientAdservingPartnerMacro_UI(
  remote: RemoteAdservingPartnerMacro_UI,
): AdservingPartnerMacro_UI {
  return {
    id: remote.id,
    dspId: remote.dspId,
    name: remote.name ?? '',
    value: remote.value ?? '',
    dspParameter: remote.dspParameter,
    allowedCampaignTypes: remote.allowedCampaignTypes ?? [],
    allowedClients: remote.allowedClients ?? [],
    isInherited: remote.isInherited,
    macroSource: remote.isInherited ? MacroSource.Parent : MacroSource.Self,
  };
}

function toRemoteAdservingPartnerMacro_UI(
  client: AdservingPartnerMacro_UI,
): RemoteAdservingPartnerMacro_UI {
  return {
    id: client.id,
    dspId: client.dspId,
    name: client.name || undefined,
    value: client.value || undefined,
    dspParameter: client.dspParameter,
    allowedCampaignTypes: client.allowedCampaignTypes?.length
      ? client.allowedCampaignTypes
      : undefined,
    allowedClients: client.allowedClients?.length ? client.allowedClients : undefined,
    isInherited: client.isInherited,
  };
}

function toClientMacros(remote: RemoteMacros_UI): Macros_UI {
  return {
    macros: (remote.macros ?? []).map(toClientAdservingPartnerMacro_UI),
  };
}

function toRemoteMacros(client: Macros_UI): RemoteMacros_UI {
  return {
    macros: client.macros.length ? client.macros.map(toRemoteAdservingPartnerMacro_UI) : undefined,
  };
}

function toClientDataSharing(remote: RemoteDataSharing_UI): DataSharing_UI {
  return {
    restrictTrackingVendors: remote.restrictTrackingVendors,
    whitelistedTrackingVendorIds: remote.whitelistedTrackingVendorIds || [],
  };
}

function toRemoteDataSharing(client: DataSharing_UI): RemoteDataSharing_UI {
  return {
    restrictTrackingVendors: client.restrictTrackingVendors,
    whitelistedTrackingVendorIds:
      client.whitelistedTrackingVendorIds.length > 0 ? client.whitelistedTrackingVendorIds : null,
  };
}

export {
  toClientAdservingPartnerInfo,
  toClientCreativeSpec,
  toClientDataSharing,
  toClientMacros,
  toLocalAdservingPartnerWithMacros,
  toLocalPartnerData,
  toRemoteAdservingPartnerInfo,
  toRemoteAdServingPartnerWithMacros,
  toRemoteCreativeSpec,
  toRemoteDataSharing,
  toRemoteMacros,
};
